// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import { TextField, TextFieldProps } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
// ----------------------------------------------------------------------

type Props = TextFieldProps & {
  name: string;
  label: string;
  status?: boolean;
  className?: string;
};

export default function RHFTimePicker({ name, label, status, value, className }: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const getTimeValue = (): Dayjs | null => {
          const timeValue = field.value || value;
          if (!timeValue) return null;

          if (dayjs.isDayjs(timeValue)) return timeValue;

          if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue)) {
            const [hours, minutes] = timeValue.split(':');
            return dayjs()
              .hour(parseInt(hours, 10))
              .minute(parseInt(minutes, 10))
              .second(0)
              .millisecond(0);
          }

          return null;
        };

        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              disabled={status}
              label={label}
              value={getTimeValue()}
              views={['hours', 'minutes']}
              inputFormat="HH:mm"
              onChange={(newValue) => {
                if (newValue && dayjs.isDayjs(newValue)) {
                  const formattedTime = newValue.format('HH:mm');
                  field.onChange(formattedTime);
                  field.onBlur();
                } else {
                  field.onChange('');
                  field.onBlur();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!error}
                  helperText={error?.message}
                  className={className}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: null,
                  }}
                  sx={{
                    '& .MuiInputBase-root.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#e0e0e0',
                      },
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: '#9e9e9e',
                      cursor: 'not-allowed',
                    },
                    '& .MuiInputLabel-root.Mui-disabled': {
                      color: '#9e9e9e',
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        );
      }}
    />
  );
}
